/**
 * Pièces communes du groupe Planifier ⑤ (vague 4 du lot U6b) : onglets du Backlog en VRAIS
 * liens (l'affichage est dans l'adresse, il se partage et s'ouvre dans un nouvel onglet), et
 * bandeau de capacité de la colonie en lecture seule (SRF-PRJ-018, AGILE-03/08).
 */
import { useId, type JSX } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bot, Columns3, List, Network, Rocket, Share2 } from 'lucide-react'

import { api } from '@/api/client'
import { MotifRefusIA, useRefusIA, type RefusIA } from '@/components/arret/RefusIA'
import { MotifRefus } from '@/components/construire/Construire'
import { ErrorState, LoadingState } from '@/components/ErrorState'
import { Button } from '@/components/ui/button'
import { useT } from '@/i18n/language'
import { compterMissionsActives } from '@/lib/planifier'
import { LIEN_LISTE } from '@/lib/cibles'

export const AFFICHAGES_BACKLOG = [
  { cle: 'tableau', label: 'Tableau', icon: Columns3 },
  { cle: 'liste', label: 'Liste', icon: List },
  { cle: 'hierarchie', label: 'Hiérarchie', icon: Network },
  { cle: 'graphe', label: 'Graphe', icon: Share2 },
  { cle: 'pret-colonie', label: 'Prêt pour la colonie', icon: Rocket },
] as const

export type AffichageBacklog = (typeof AFFICHAGES_BACKLOG)[number]['cle']

export function estAffichageBacklog(v: string | null): v is AffichageBacklog {
  return AFFICHAGES_BACKLOG.some((a) => a.cle === v)
}

/** Onglets du Backlog : une navigation de liens, l'affichage courant porte `aria-current`. */
export function OngletsBacklog({ courant }: { courant: AffichageBacklog }): JSX.Element {
  const { t } = useT()
  return (
    <nav aria-label={t('planifier.affichagesBacklog', 'Affichages du backlog')} data-testid="onglets-backlog">
      <ul className="flex flex-wrap gap-1 rounded-lg bg-muted p-1">
        {AFFICHAGES_BACKLOG.map((a) => {
          const actif = a.cle === courant
          return (
            <li key={a.cle}>
              <Link to={`/projet?vue=${a.cle}`} data-testid={`vue-${a.cle}`}
                    aria-current={actif ? 'page' : undefined}
                    className={`inline-flex min-h-11 items-center gap-1.5 rounded-md px-3 text-sm font-medium sm:min-h-8 ${
                      actif ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
                <a.icon className="size-4" aria-hidden="true" />
                {t('projet.vue.' + a.cle, a.label)}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * Capacité de la colonie, lecture seule : missions actives du projet. « Prêtes » attend le
 * calcul serveur de préparation — dit tel quel, jamais un zéro inventé. Ne bloque rien.
 */
export function BandeauCapaciteColonie({ workspaceId }: { workspaceId: string }): JSX.Element {
  const { t } = useT()
  const missions = useQuery({
    queryKey: ['missions', workspaceId, 'capacite-colonie'],
    queryFn: () => api.listMissions(workspaceId),
  })
  return (
    <section aria-labelledby="capacite-colonie-titre" data-testid="capacite-colonie"
             className="space-y-1 rounded-lg border bg-muted/30 p-3 text-sm">
      <h2 id="capacite-colonie-titre" className="flex items-center gap-2 font-semibold">
        <Bot className="size-4" aria-hidden="true" />
        {t('planifier.capaciteTitre', 'Capacité de la colonie — lecture seule')}
      </h2>
      {missions.isPending ? <LoadingState label={t('planifier.capaciteChargement', 'Lecture des missions…')} /> : null}
      {missions.isError ? (
        <ErrorState message={t('planifier.capaciteErreur', 'Capacité de la colonie illisible : la liste des missions n’a pas répondu.')}
                    onRetry={() => void missions.refetch()} />
      ) : null}
      {missions.data ? (
        <p data-testid="capacite-colonie-compte">
          {t('planifier.capaciteCompte', 'Colonie — prêtes : non calculé · {{n}} mission(s) active(s)', { n: compterMissionsActives(missions.data) })}
          {' · '}
          <Link to="/missions" className={LIEN_LISTE}>{t('planifier.voirMissions', 'Voir les missions')}</Link>
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {t('planifier.capaciteNote', 'Ce bandeau ne bloque rien : il dit ce que la colonie a en cours. Le compte des features prêtes attend le contrôle de préparation côté serveur.')}
      </p>
    </section>
  )
}

/**
 * FND-V050-113 — « Lancer une mission sur un sprint » : le worker EXÉCUTE les missions de sprint
 * (nature d'itération, FND-V050-052) ; l'ancien refus « non exécuté (G4) » était périmé et
 * contredisait le formulaire. Le lien ouvre le formulaire sur la nature « sprint », qui dit avant
 * le lancement l'application livrée et sa révision, ou refuse s'il n'y en a aucune
 * (MISSION_APP_NOT_DELIVERED). Pendant l'arrêt de l'IA : désactivé, motif de l'arrêt.
 */
export function LancerMissionSprint({ sprintId }: { sprintId?: number } = {}): JSX.Element {
  const { t } = useT()
  // Depuis la page d'UN sprint, le lien porte son identifiant : le formulaire s'ouvre dessus.
  const cible = `/missions?vue=lancer&nature=sprint${sprintId != null ? `&sprint=${sprintId}` : ''}`
  const refusIA = useRefusIA()
  const idNote = useId()
  const libelle = t('planifier.lancerSprint', 'Lancer une mission sur ce sprint')
  return (
    <section className="space-y-2 rounded-lg border p-3" data-testid="lancer-sprint">
      {refusIA.refuse ? (
        <Button size="sm" disabled aria-describedby={`${idNote} ${refusIA.idMotif}`} data-testid="lancer-sprint-bouton">
          {libelle}
        </Button>
      ) : (
        <Button asChild size="sm">
          <Link to={cible} aria-describedby={idNote} data-testid="lancer-sprint-bouton">
            <Rocket className="mr-1 size-3" aria-hidden="true" />{libelle}
          </Link>
        </Button>
      )}
      <p id={idNote} role="note" className="text-sm text-muted-foreground" data-testid="lancer-sprint-note">
        {t('planifier.lancerSprintNote', 'Une mission de sprint itère sur l’application déjà livrée de ce projet, à partir de sa dernière révision livrée : le formulaire les montre, et refuse avant le lancement si aucune application n’est livrée.')}
      </p>
      <MotifRefusIA refus={refusIA} />
    </section>
  )
}

/**
 * SRF-PRJ-005 — « Lancer une mission sur cette tranche », par feature (planche ProduitTranche05x ②).
 * Une tranche EST une mission de sprint sur un sujet plus étroit : le lien ouvre LE lanceur
 * (SRF-MIS-005, jamais un parcours séparé), prérempli (`nature=sprint&tranche=<feature>`).
 * Refusé AVANT le clic, motif nommé : tranche non prête (SLICE_NOT_READY, avec ses manques),
 * préparation non évaluée par la passerelle, arrêt de l'IA. La passerelle reste seule juge.
 */
export type EvaluationTranche = 'evaluee' | 'en-cours' | 'indisponible'

export function LancerTranche({ featureId, titre, etat, manques, evaluation, refusIA }: {
  featureId: number
  titre: string
  etat: 'faite' | 'prete' | 'non-prete' | 'non-evaluee'
  manques: readonly string[]
  evaluation: EvaluationTranche
  refusIA: RefusIA
}): JSX.Element | null {
  const { t } = useT()
  if (etat === 'faite') return null
  const libelle = t('planifier.lancerTranche', 'Lancer une mission sur cette tranche')
  const nom = t('planifier.lancerTrancheNom', 'Lancer une mission sur la tranche #{{id}} « {{titre}} »', { id: featureId, titre })
  const idMotif = `lancer-tranche-motif-${featureId}`
  const prete = evaluation === 'evaluee' && etat === 'prete'
  if (prete && !refusIA.refuse) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to={`/missions?vue=lancer&nature=sprint&tranche=${encodeURIComponent(String(featureId))}`}
              aria-label={nom} data-testid={`lancer-tranche-${featureId}`}>
          <Rocket className="mr-1 size-3" aria-hidden="true" />{libelle}
        </Link>
      </Button>
    )
  }
  // Motif propre à la ligne (tranche non prête) ; les motifs communs (évaluation, arrêt de l'IA)
  // sont dits une fois sous le tableau et reliés par `aria-describedby`.
  const decrit = [
    evaluation === 'evaluee' && etat !== 'prete' ? idMotif : null,
    evaluation !== 'evaluee' ? 'lancer-tranche-indisponible' : null,
    refusIA.refuse ? refusIA.idMotif : null,
  ].filter(Boolean).join(' ')
  return (
    <div className="space-y-1">
      <Button size="sm" variant="outline" disabled aria-label={nom} aria-describedby={decrit || undefined}
              data-testid={`lancer-tranche-${featureId}`}>
        <Rocket className="mr-1 size-3" aria-hidden="true" />{libelle}
      </Button>
      {evaluation === 'evaluee' && etat !== 'prete' ? (
        <MotifRefus id={idMotif} code="SLICE_NOT_READY" testid={idMotif}>
          {t('planifier.trancheNonPrete', 'La tranche n’est pas prête : {{n}} manque(s).', { n: manques.length })}
          {manques.length > 0 ? ` ${manques.join(' · ')}` : ''}
        </MotifRefus>
      ) : null}
    </div>
  )
}

/** Motifs communs du lancement par tranche : préparation non évaluée, arrêt de l'IA. */
export function MotifsLancerTranche({ evaluation, refusIA }: { evaluation: EvaluationTranche; refusIA: RefusIA }): JSX.Element {
  const { t } = useT()
  return (
    <section className="space-y-2 rounded-lg border p-3" data-testid="lancer-tranche">
      <p className="text-sm text-muted-foreground" data-testid="lancer-tranche-note">
        {t('planifier.lancerTrancheNote', 'Une mission sur une tranche est une mission de sprint sur une feature PRÊTE et ses stories : « Lancer une mission sur cette tranche » ouvre le lanceur prérempli. La reine propose ensuite le plan, qu’un autre membre approuve.')}
      </p>
      {evaluation !== 'evaluee' ? (
        <p id="lancer-tranche-indisponible" role="note" className="text-sm text-[var(--color-error-text)]" data-testid="lancer-tranche-indisponible">
          {evaluation === 'en-cours'
            ? t('planifier.trancheEvaluationEnCours', 'Évaluation de la préparation en cours : le lancement attend de savoir si la tranche est prête.')
            : t('planifier.trancheEvaluationIndisponible', 'Préparation non évaluée par la passerelle : impossible de dire qu’une tranche est prête, le lancement est désactivé.')}
        </p>
      ) : null}
      <MotifRefusIA refus={refusIA} />
    </section>
  )
}
